import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Star, Save, Copy, MessageCircle, ExternalLink, Info, BellRing, Check } from "lucide-react";

const STAR_FILL = "#f59e0b";

function FeedbackAvgTile({ participants }) {
  const feedbacks = (participants || []).map(p => p.feedback).filter(Boolean);
  if (feedbacks.length === 0) return null;
  const avg = (feedbacks.reduce((s, f) => s + (Number(f.rating) || 0), 0) / feedbacks.length).toFixed(1);
  const responseRate = Math.round((feedbacks.length / participants.length) * 100);
  const trustpiloted = feedbacks.filter(f => f.redirected_external).length;
  return (
    <div className="aii-card p-4 mb-3 flex items-center gap-4 flex-wrap" data-testid="fb-avg-tile">
      <div className="flex items-center gap-2">
        <Star size={22} className="text-amber-500" fill={STAR_FILL} strokeWidth={1.5}/>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Average rating</div>
          <div className="text-2xl font-display font-semibold text-brand" data-testid="fb-avg">{avg}<span className="text-sm text-[color:var(--aii-muted)] font-normal">/5</span></div>
        </div>
      </div>
      <div className="border-l border-[color:var(--aii-border)] h-10"/>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Response rate</div>
        <div className="text-lg font-display font-semibold" data-testid="fb-response-rate">{responseRate}% <span className="text-xs text-[color:var(--aii-muted)] font-normal">({feedbacks.length}/{participants.length})</span></div>
      </div>
      {trustpiloted > 0 && (
        <>
          <div className="border-l border-[color:var(--aii-border)] h-10"/>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">On Trust Pilot / Google</div>
            <div className="text-lg font-display font-semibold text-emerald-700" data-testid="fb-external">{trustpiloted}</div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Partner Portal · Feedback tab.
 *
 * Lets each partner:
 *  1. Store their public review URL (Trustpilot / Google Business / etc.).
 *     Participants NEVER see this URL — it's only used server-side to
 *     redirect 4-5★ raters to leave a public review.
 *  2. See every booked/approved participant on their account with a
 *     "Copy feedback link" button.  The link points to /feedback/{bid}
 *     — a public, no-login page where the participant taps stars.
 *  3. See feedback that already landed (rating, comment, and whether it
 *     bounced to the external review site).
 */
export default function FeedbackTab() {

  // FeedbackAvgTile — compact rollup showing avg rating (out of 5) + response
  // rate + Trust-Pilot-forward count for THIS partner. Renders nothing when
  // there are zero feedback rows to avoid a "0/5" first impression.
  const [participants, setParticipants] = useState([]);
  const [reviewLink, setReviewLink] = useState("");
  const [savedLink, setSavedLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner-portal/feedback");
      setParticipants(data.participants || []);
      setReviewLink(data.review_link || "");
      setSavedLink(data.review_link || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load feedback"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveLink = async () => {
    setBusy(true);
    try {
      await api.patch("/partner-portal/profile", { review_link: reviewLink });
      toast.success("Review link saved");
      setSavedLink(reviewLink);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const [requesting, setRequesting] = useState(null); // booking_id currently being requested
  const requestFeedback = async (bid) => {
    setRequesting(bid);
    try {
      const { data } = await api.post(`/partner-portal/feedback/${bid}/request`);
      toast.success(data.participant_notified
        ? "Request sent — the participant will see it on their Home tab"
        : "Request logged — this participant has no login yet");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
    setRequesting(null);
  };

  const linkDirty = reviewLink !== savedLink;

  return (
    <div className="space-y-4" data-testid="partner-feedback-tab">
      <div className="aii-card p-5 border-l-4 border-brand">
        <div className="flex items-start gap-2 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold flex items-center gap-1.5">
              <Star size={14} className="text-amber-500" fill={STAR_FILL} strokeWidth={1.5}/> Your public review link
            </div>
            <p className="text-xs text-[color:var(--aii-muted)] mt-1 leading-relaxed">
              Paste your Trustpilot, Google Business, or other public review URL. Participants who rate you <b>4 or 5 stars</b> are automatically forwarded to leave a public review there. <b>Participants never see this URL</b> — only the star rating and (for lower ratings) a private suggestion box.
            </p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <input
            data-testid="fb-review-link"
            value={reviewLink}
            onChange={e => setReviewLink(e.target.value)}
            placeholder="https://www.trustpilot.com/evaluate/yourdomain.com"
            className="input-aii flex-1 min-w-[280px]"
          />
          <button data-testid="fb-save-link" onClick={saveLink} disabled={busy || !linkDirty}
                  className="px-3 py-1.5 bg-brand text-white text-sm font-semibold rounded hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5">
            <Save size={12}/> {busy ? "Saving…" : linkDirty ? "Save" : "Saved"}
          </button>
        </div>
        {!savedLink && (
          <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-start gap-1.5">
            <Info size={11} className="mt-0.5 flex-shrink-0"/>
            No public review link yet — 4-5★ raters will land on a plain thank-you page until you add one.
          </div>
        )}
      </div>

      <div>
        <FeedbackAvgTile participants={participants} />
        <h3 className="font-semibold mb-2 flex items-center gap-2">Participants
          <span className="text-[11px] text-[color:var(--aii-muted)] font-normal">({participants.length})</span>
        </h3>
        {loading ? (
          <div className="aii-card p-6 text-center text-sm text-[color:var(--aii-muted)] italic">Loading…</div>
        ) : participants.length === 0 ? (
          <div className="aii-card p-6 text-center text-sm text-[color:var(--aii-muted)] italic" data-testid="fb-empty">
            No participants yet — approved applications and staff-created bookings will appear here for feedback collection.
          </div>
        ) : (
          <div className="space-y-2">
            {participants.map(p => {
              const fb = p.feedback;
              return (
                <div key={p.booking_id} data-testid={`fb-row-${p.booking_id}`}
                     className="aii-card p-4 flex items-start justify-between gap-3 flex-wrap hover:border-brand transition">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-base truncate">{p.name || "—"}</div>
                    <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">
                      {[p.programme, p.start_date].filter(Boolean).join(" · ") || "—"}
                    </div>
                    {fb ? (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <div className="flex items-center" data-testid={`fb-rating-${p.booking_id}`}>
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} size={13}
                                  className={s <= fb.rating ? "text-amber-500" : "text-slate-300"}
                                  fill={s <= fb.rating ? STAR_FILL : "none"}
                                  strokeWidth={1.5}/>
                          ))}
                          <span className="ml-1.5 text-xs font-semibold text-[color:var(--aii-text-2)]">{fb.rating}/5</span>
                        </div>
                        {fb.redirected_external ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold uppercase flex items-center gap-1 border border-emerald-200">
                            <ExternalLink size={9}/> Rated on Trust&nbsp;Pilot / Google
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold uppercase border border-amber-200">
                            Feedback submitted
                          </span>
                        )}
                        {fb.comment && (
                          <div className="w-full mt-1 text-[11px] text-[color:var(--aii-text-2)] bg-[color:var(--aii-bg)] border border-[color:var(--aii-border)] rounded px-2 py-1.5 flex items-start gap-1.5">
                            <MessageCircle size={11} className="mt-0.5 flex-shrink-0 text-[color:var(--aii-muted)]"/>
                            <span className="whitespace-pre-wrap">{fb.comment}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-1 text-[11px] text-[color:var(--aii-muted)] italic">Not yet rated</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {fb ? (
                      <span className="text-[10px] text-[color:var(--aii-muted)] italic">Rated</span>
                    ) : p.requested_at ? (
                      <>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 font-bold uppercase border border-sky-200 flex items-center gap-1" data-testid={`fb-requested-${p.booking_id}`}>
                          <Check size={9}/> Requested
                        </span>
                        <button data-testid={`fb-request-again-${p.booking_id}`}
                                onClick={() => requestFeedback(p.booking_id)}
                                disabled={requesting === p.booking_id}
                                className="text-[10px] text-[color:var(--aii-muted)] hover:text-brand underline disabled:opacity-50">
                          {requesting === p.booking_id ? "Sending…" : "Send reminder"}
                        </button>
                      </>
                    ) : (
                      <button data-testid={`fb-request-${p.booking_id}`}
                              onClick={() => requestFeedback(p.booking_id)}
                              disabled={requesting === p.booking_id}
                              className="text-xs px-3 py-1.5 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1.5 font-semibold whitespace-nowrap disabled:opacity-50">
                        <BellRing size={12}/> {requesting === p.booking_id ? "Sending…" : "Request feedback"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
